'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { signIn, getSession } from 'next-auth/react';
import { HardHat, AlertCircle, Loader2 } from 'lucide-react';
import { useBrand } from '@/components/providers/BrandProvider';

type RosterWorker = { id: string; name: string; hasCode: boolean };

export default function WorkerLoginForm() {
  const router = useRouter();
  const brand = useBrand();
  const [roster, setRoster] = useState<RosterWorker[]>([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [selected, setSelected] = useState<RosterWorker | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Auto-prisijungimas: jei jau yra galiojanti darbuotojo sesija (pvz. perkrovus
  // app), iškart nukreipiam į programėlę — nereikia vesti kodo iš naujo.
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let alive = true;
    void getSession()
      .then((s) => {
        if (!alive) return;
        const role = (s?.user as { role?: string } | undefined)?.role;
        if (s?.user && role === 'WORKER') {
          router.replace('/worker-mobile-app?tab=tasks');
          return; // liekam „kraunama" būsenoje, nes naviguojam
        }
        setCheckingSession(false);
      })
      .catch(() => {
        if (alive) setCheckingSession(false);
      });
    return () => {
      alive = false;
    };
  }, [router]);

  useEffect(() => {
    void fetch('/api/workers/kiosk-roster')
      .then(async (r) => {
        const json = await r.json().catch(() => ({}));
        if (!r.ok) {
          setError(typeof json.error === 'string' ? json.error : 'Nepavyko užkrauti darbuotojų sąrašo');
          return;
        }
        setRoster(json.data ?? []);
      })
      .catch(() => setError('Nepavyko užkrauti darbuotojų sąrašo'))
      .finally(() => setRosterLoading(false));
  }, []);

  const submitPin = async () => {
    if (!selected) return;
    if (!/^\d{6}$/.test(pin)) {
      setError('Įveskite 6 skaitmenų PIN');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const entryRes = await fetch('/api/attendance/kiosk-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workerId: selected.id, pin }),
      });
      const entryJson = await entryRes.json().catch(() => ({}));
      if (!entryRes.ok) {
        setError(typeof entryJson.error === 'string' ? entryJson.error : 'Neteisingas PIN');
        return;
      }

      const { workerCode } = entryJson.data ?? {};
      const authRes = await signIn('credentials', { workerCode, redirect: false });
      if (authRes?.error) {
        setError('Prisijungimo klaida');
        return;
      }

      for (let i = 0; i < 15; i++) {
        const session = await getSession();
        if (session?.user) break;
        await new Promise((r) => setTimeout(r, 150));
      }

      router.replace('/worker-mobile-app?tab=tasks');
    } catch {
      setError('Prisijungimo klaida. Bandykite dar kartą.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-[#f0f4f8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f4f8] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/api/logo" alt={brand.name} className="h-12 w-auto mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-emerald-800">Meistrai</h1>
          <p className="text-sm text-slate-500 mt-1">
            {selected ? `Sveiki, ${selected.name}` : 'Pasirinkite savo vardą'}
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-red-700 text-sm mb-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {!selected ? (
          <div className="card p-3 space-y-2 max-h-[60vh] overflow-y-auto">
            {rosterLoading ? (
              <div className="flex justify-center py-10 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : roster.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Darbuotojų sąrašas tuščias</p>
            ) : (
              roster.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => {
                    setSelected(w);
                    setPin('');
                    setError(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:bg-emerald-50 hover:border-emerald-200 text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {w.name.slice(0, 1).toUpperCase()}
                  </div>
                  <span className="text-sm font-semibold text-stone-900">{w.name}</span>
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="card p-6 space-y-4">
            <div>
              <label className="label-text">6 skaitmenų PIN</label>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                autoFocus
                placeholder="••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input-field text-center text-2xl font-bold tracking-[0.3em] tabular-nums"
              />
            </div>
            <button
              type="button"
              disabled={loading || pin.length !== 6}
              onClick={() => void submitPin()}
              className="w-full bg-emerald-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Jungiamasi...
                </>
              ) : (
                'Prisijungti'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setPin('');
                setError(null);
              }}
              className="w-full py-2 text-xs text-slate-500 font-semibold"
            >
              ← Kitas darbuotojas
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
